import React, { useState, useEffect } from 'react';
import {
  Modal, Form, Button, Alert, Spinner, Badge, Card, Row, Col, Tabs, Tab
} from 'react-bootstrap';
import { FaUser, FaEnvelope, FaLock, FaEye, FaEyeSlash, FaCheck, FaUserTie, FaInfoCircle, FaExclamationTriangle } from 'react-icons/fa';
import { useAuth } from '../../hooks/useAuth';
import supabase from '../../utils/supabaseClient';

const UserModal = ({ 
  show, 
  onHide, 
  modalType = 'create',
  selectedUser = null,
  systems = [],
  systemRoles = [],
  onSuccess = () => {}
}) => {
  const { user } = useAuth();

  // Estados do formulário
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    confirmPassword: '',
    active: true,
    systemAccesses: {},
    vendedor_id: null // Novo campo para vendedor
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [vendedores, setVendedores] = useState([]); // Novo estado para vendedores

  // Estados de validação
  const [emailValidation, setEmailValidation] = useState({
    isValid: true,
    message: '',
    isChecking: false
  });

  const [passwordValidation, setPasswordValidation] = useState({
    isValid: true,
    errors: []
  });

  // Estados para validação de vendedor
  const [vendedorValidation, setVendedorValidation] = useState({
    isValid: true,
    message: '',
    isChecking: false
  });

  // Carregar vendedores
  useEffect(() => {
    if (show) {
      loadVendedores();
    }
  }, [show]);

  const loadVendedores = async () => {
    try {
      const { data, error } = await supabase
        .from('vendedores')
        .select('*')
        .eq('ativo', true)
        .order('descricao');

      if (error) throw error;
      setVendedores(data || []);
    } catch (error) {
      console.error('Erro ao carregar vendedores:', error);
    }
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (show) {
      if (modalType === 'edit' && selectedUser) {
        setFormData({
          email: selectedUser.email || '',
          full_name: selectedUser.full_name || '',
          password: '',
          confirmPassword: '',
          active: selectedUser.active !== false,
          systemAccesses: {},
          vendedor_id: null
        });
        
        loadUserCurrentAccess(selectedUser.id);
        loadUserVendedor(selectedUser.id); // Carregar vendedor vinculado
      } else {
        resetForm();
      }
      setError(null);
      setEmailValidation({ isValid: true, message: '', isChecking: false });
      setPasswordValidation({ isValid: true, errors: [] });
      setVendedorValidation({ isValid: true, message: '', isChecking: false });
    }
  }, [show, modalType, selectedUser]);

  const resetForm = () => {
    setFormData({
      email: '',
      full_name: '',
      password: '',
      confirmPassword: '',
      active: true,
      systemAccesses: {},
      vendedor_id: null
    });
  };

  const loadUserCurrentAccess = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('user_system_access')
        .select('system_id, role_id')
        .eq('profile_id', userId)
        .eq('active', true);

      if (error) throw error;

      const accesses = {};
      data?.forEach(access => {
        accesses[access.system_id] = access.role_id;
      });

      setFormData(prev => ({
        ...prev,
        systemAccesses: accesses
      }));
    } catch (error) {
      console.error('Erro ao carregar acessos atuais:', error);
    }
  };

  // Nova função para carregar vendedor vinculado
  const loadUserVendedor = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('user_vendedores')
        .select('vendedor_id')
        .eq('profile_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        setFormData(prev => ({
          ...prev,
          vendedor_id: data.vendedor_id
        }));
      }
    } catch (error) {
      console.error('Erro ao carregar vendedor vinculado:', error);
    }
  };

  // Função para verificar se vendedor já está vinculado
  const checkVendedorExists = async (vendedorId, excludeUserId = null) => {
    if (!vendedorId) {
      setVendedorValidation({ isValid: true, message: '', isChecking: false });
      return;
    }

    setVendedorValidation(prev => ({ ...prev, isChecking: true }));

    try {
      let query = supabase
        .from('user_vendedores')
        .select(`
          profile_id,
          profiles:profile_id(id, full_name)
        `)
        .eq('vendedor_id', vendedorId);

      // Na edição, excluir o usuário atual da verificação
      if (excludeUserId) {
        query = query.neq('profile_id', excludeUserId);
      }

      const { data: existingVinculo, error } = await query.single();

      if (error && error.code !== 'PGRST116') {
        console.error('Erro ao verificar vendedor:', error);
        setVendedorValidation({ isValid: true, message: '', isChecking: false });
        return;
      }

      if (existingVinculo) {
        setVendedorValidation({
          isValid: false,
          message: `Vendedor já está vinculado ao usuário ${existingVinculo.profiles?.full_name}`,
          isChecking: false
        });
      } else {
        setVendedorValidation({
          isValid: true,
          message: 'Vendedor disponível',
          isChecking: false
        });
      }
    } catch (error) {
      console.error('Erro ao verificar vendedor:', error);
      setVendedorValidation({ isValid: true, message: '', isChecking: false });
    }
  };

  // Verificar se tem role de vendedor selecionado
  const hasVendedorRole = () => {
    const vendedorSystemRole = systemRoles.find(role => 
      role.name?.toLowerCase() === 'vendedor' || 
      role.description?.toLowerCase().includes('vendedor')
    );
    
    if (!vendedorSystemRole) return false;
    
    return Object.values(formData.systemAccesses).includes(vendedorSystemRole.id);
  };

  // Effect para verificar vendedor quando selecionado
  useEffect(() => {
    if (formData.vendedor_id && hasVendedorRole()) {
      const excludeUserId = modalType === 'edit' ? selectedUser?.id : null;
      const timeoutId = setTimeout(() => {
        checkVendedorExists(formData.vendedor_id, excludeUserId);
      }, 500);

      return () => clearTimeout(timeoutId);
    } else {
      setVendedorValidation({ isValid: true, message: '', isChecking: false });
    }
  }, [formData.vendedor_id, formData.systemAccesses, modalType, selectedUser?.id]);

  // Validação de email
  useEffect(() => {
    if (modalType === 'create' && formData.email) {
      const timeoutId = setTimeout(() => {
        checkEmailExists(formData.email);
      }, 800);

      return () => clearTimeout(timeoutId);
    } else {
      setEmailValidation({ isValid: true, message: '', isChecking: false });
    }
  }, [formData.email, modalType]);

  // Validação de senha
  useEffect(() => {
    if (formData.password) {
      validatePassword(formData.password);
    } else {
      setPasswordValidation({ isValid: true, errors: [] });
    }
  }, [formData.password]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const checkEmailExists = async (email) => {
    if (!email || !validateEmail(email)) {
      return;
    }

    setEmailValidation(prev => ({ ...prev, isChecking: true }));

    try {
      const { data: existingUser, error } = await supabase
        .from('profiles')
        .select('id, full_name')
        .eq('email', email)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Erro ao verificar email:', error);
        return;
      }

      if (existingUser) {
        setEmailValidation({
          isValid: false,
          message: `Email já está sendo usado por ${existingUser.full_name}`,
          isChecking: false
        });
      } else {
        setEmailValidation({
          isValid: true,
          message: 'Email disponível',
          isChecking: false
        });
      }
    } catch (error) {
      console.error('Erro ao verificar email:', error);
      setEmailValidation({
        isValid: true,
        message: '',
        isChecking: false
      });
    }
  };

  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 8) {
      errors.push('Mínimo de 8 caracteres');
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('Pelo menos uma letra maiúscula');
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push('Pelo menos uma letra minúscula');
    }
    
    if (!/\d/.test(password)) {
      errors.push('Pelo menos um número');
    }
    
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Pelo menos um caractere especial');
    }

    setPasswordValidation({
      isValid: errors.length === 0,
      errors
    });
  };

  const handleSystemAccessChange = (systemId, roleId) => {
    setFormData(prev => {
      const newSystemAccesses = {
        ...prev.systemAccesses,
        [systemId]: roleId || undefined
      };
      
      // Se removeu o role de vendedor, limpar vendedor
      const vendedorRole = systemRoles.find(role => 
        role.name?.toLowerCase() === 'vendedor' || 
        role.description?.toLowerCase().includes('vendedor')
      );
      
      if (vendedorRole && roleId != vendedorRole.id && prev.systemAccesses[systemId] === vendedorRole.id) {
        return {
          ...prev,
          systemAccesses: newSystemAccesses,
          vendedor_id: null
        };
      }
      
      return {
        ...prev,
        systemAccesses: newSystemAccesses
      };
    });
  };

  const grantSystemAccess = async (profileId, systemSlug, roleName, grantedBy) => {
    try {
      // Buscar sistema e role
      const { data: system } = await supabase
        .from('systems')
        .select('id')
        .eq('slug', systemSlug)
        .single();

      const { data: role } = await supabase
        .from('system_roles')
        .select('id')
        .eq('system_id', system.id)
        .eq('name', roleName)
        .single();

      // Inserir ou atualizar acesso
      const { error } = await supabase
        .from('user_system_access')
        .upsert({
          profile_id: profileId,
          system_id: system.id,
          role_id: role.id,
          active: true,
          granted_by: grantedBy,
          granted_at: new Date().toISOString()
        }, { 
          onConflict: 'profile_id,system_id' 
        });

      if (error) throw error;

      // Log da ação
      await supabase
        .from('user_access_log')
        .insert({
          profile_id: profileId,
          system_id: system.id,
          action: 'granted',
          new_role_id: role.id,
          changed_by: grantedBy,
          reason: `Acesso concedido via administração`
        });

    } catch (error) {
      console.error('Erro ao conceder acesso:', error);
      throw error;
    }
  };

  // Função para gerenciar vinculação de vendedor
  const manageVendedorVinculacao = async (profileId, vendedorId) => {
    try {
      // Primeiro, remover vinculação existente
      await supabase
        .from('user_vendedores')
        .delete()
        .eq('profile_id', profileId);

      // Se há vendedor selecionado, criar nova vinculação
      if (vendedorId) {
        const { error: vendedorError } = await supabase
          .from('user_vendedores')
          .insert({
            profile_id: profileId,
            vendedor_id: vendedorId
          });

        if (vendedorError) {
          console.error('Erro ao vincular vendedor:', vendedorError);
          
          if (vendedorError.code === '23505' && vendedorError.message?.includes('user_vendedores_vendedor_id_key')) {
            const vendedorNome = getVendedorName(vendedorId);
            throw new Error(`O vendedor "${vendedorNome}" já está vinculado a outro usuário.`);
          }
          
          throw new Error('Erro ao vincular vendedor: ' + vendedorError.message);
        }
      }
    } catch (error) {
      console.error('Erro ao gerenciar vinculação de vendedor:', error);
      throw error;
    }
  };

  const handleCreateUser = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validações
      if (!formData.email || !formData.password || !formData.full_name) {
        throw new Error('Todos os campos são obrigatórios');
      }

      if (!validateEmail(formData.email)) {
        throw new Error('Por favor, insira um email válido.');
      }

      if (!emailValidation.isValid) {
        throw new Error('O email informado já está sendo usado por outro usuário.');
      }

      if (!passwordValidation.isValid) {
        throw new Error('A senha não atende aos requisitos de segurança.');
      }

      if (formData.password !== formData.confirmPassword) {
        throw new Error('As senhas não coincidem');
      }

      const selectedSystems = Object.entries(formData.systemAccesses)
        .filter(([_, roleId]) => roleId);
      
      if (selectedSystems.length === 0) {
        throw new Error('É obrigatório conceder acesso a pelo menos um sistema');
      }

      // Validação para role vendedor
      if (hasVendedorRole() && !formData.vendedor_id) {
        throw new Error('É obrigatório selecionar um vendedor quando o role "Vendedor" está selecionado');
      }

      if (formData.vendedor_id && !vendedorValidation.isValid) {
        throw new Error('O vendedor selecionado já está vinculado a outro usuário.');
      }

      // Criar usuário no Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            full_name: formData.full_name
          }
        }
      });

      if (authError) {
        if (authError.message?.includes('User already registered')) {
          throw new Error(`O email "${formData.email}" já está cadastrado no sistema.`);
        }
        throw new Error(`Erro na autenticação: ${authError.message}`);
      }

      if (!authData.user) {
        throw new Error('Erro ao criar usuário');
      }

      const newUserId = authData.user.id;

      // Aguardar criação do perfil
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Criar/atualizar perfil
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          id: newUserId,
          email: formData.email,
          full_name: formData.full_name,
          active: formData.active
        });

      if (profileError) {
        console.error('Erro ao criar perfil:', profileError);
      }

      // Conceder acessos aos sistemas
      for (const [systemId, roleId] of selectedSystems) {
        const system = systems.find(s => s.id === parseInt(systemId));
        const role = systemRoles.find(r => r.id === parseInt(roleId));
        
        if (system && role) {
          await grantSystemAccess(newUserId, system.slug, role.name, user.id);
        }
      }

      // Gerenciar vinculação de vendedor se necessário
      if (formData.vendedor_id) {
        await manageVendedorVinculacao(newUserId, formData.vendedor_id);
      }

      const vendedorInfo = formData.vendedor_id ? 
        ` e vinculado ao vendedor ${getVendedorName(formData.vendedor_id)} (role Vendedor)` : '';

      onSuccess(`Usuário criado com sucesso com acesso a ${selectedSystems.length} sistema(s)${vendedorInfo}!`);
      onHide();

    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!formData.email || !formData.full_name) {
        throw new Error('Nome e email são obrigatórios');
      }

      // Validação para role vendedor
      if (hasVendedorRole() && !formData.vendedor_id) {
        throw new Error('É obrigatório selecionar um vendedor quando o role "Vendedor" está selecionado');
      }

      if (formData.vendedor_id && !vendedorValidation.isValid) {
        throw new Error('O vendedor selecionado já está vinculado a outro usuário.');
      }

      // Atualizar perfil
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          email: formData.email,
          full_name: formData.full_name,
          active: formData.active
        })
        .eq('id', selectedUser.id);

      if (profileError) throw profileError;

      // Remover acessos atuais
      const { error: revokeError } = await supabase
        .from('user_system_access')
        .update({ active: false })
        .eq('profile_id', selectedUser.id);

      if (revokeError) throw revokeError;

      // Conceder novos acessos
      const selectedSystems = Object.entries(formData.systemAccesses)
        .filter(([_, roleId]) => roleId);

      for (const [systemId, roleId] of selectedSystems) {
        const system = systems.find(s => s.id === parseInt(systemId));
        const role = systemRoles.find(r => r.id === parseInt(roleId));
        
        if (system && role) {
          await grantSystemAccess(selectedUser.id, system.slug, role.name, user.id);
        }
      }

      // Gerenciar vinculação de vendedor
      await manageVendedorVinculacao(selectedUser.id, formData.vendedor_id);

      const vendedorInfo = formData.vendedor_id ? 
        ` e vinculado ao vendedor ${getVendedorName(formData.vendedor_id)} (role Vendedor)` : '';

      onSuccess(`Usuário atualizado com sucesso! Acesso concedido a ${selectedSystems.length} sistema(s)${vendedorInfo}.`);
      onHide();

    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      setError('Erro ao atualizar usuário: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getVendedorName = (vendedorId) => {
    const vendedor = vendedores.find(v => v.id === vendedorId);
    return vendedor ? vendedor.descricao : 'Vendedor não encontrado';
  };

  const isFormValid = () => {
    if (modalType === 'create') {
      return (
        formData.full_name && 
        formData.email && 
        validateEmail(formData.email) &&
        emailValidation.isValid &&
        !emailValidation.isChecking &&
        formData.password &&
        formData.confirmPassword &&
        passwordValidation.isValid &&
        formData.password === formData.confirmPassword &&
        Object.values(formData.systemAccesses).some(roleId => roleId) &&
        (!hasVendedorRole() || (formData.vendedor_id && vendedorValidation.isValid))
      );
    } else {
      return (
        formData.full_name && 
        formData.email &&
        Object.values(formData.systemAccesses).some(roleId => roleId) &&
        (!hasVendedorRole() || (formData.vendedor_id && vendedorValidation.isValid))
      );
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {modalType === 'create' ? 'Novo Usuário' : 'Editar Usuário'}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body className="p-0">
        {error && (
          <div className="px-4 pt-3">
            <Alert variant="danger">{error}</Alert>
          </div>
        )}
        
        <Tabs defaultActiveKey="dados">
          <Tab eventKey="dados" title="👤 Dados Pessoais">
            <div className="p-4">
              <Row className="g-4">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Nome Completo *</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.full_name}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        full_name: e.target.value
                      }))}
                      placeholder="Digite o nome completo"
                    />
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Email *</Form.Label>
                    <div className="position-relative">
                      <Form.Control
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          email: e.target.value
                        }))}
                        placeholder="usuario@empresa.com"
                        className={
                          modalType === 'create' && formData.email
                            ? emailValidation.isValid
                              ? emailValidation.message === 'Email disponível' ? 'border-success' : ''
                              : 'border-danger'
                            : ''
                        }
                        disabled={modalType === 'edit'}
                      />
                      {modalType === 'create' && formData.email && emailValidation.isChecking && (
                        <div className="position-absolute top-50 end-0 translate-middle-y pe-3">
                          <Spinner animation="border" size="sm" />
                        </div>
                      )}
                    </div>
                    {modalType === 'create' && formData.email && emailValidation.message && (
                      <Form.Text className={emailValidation.isValid ? 'text-success' : 'text-danger'}>
                        {emailValidation.isValid ? '✅' : '❌'} {emailValidation.message}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              {modalType === 'create' && (
                <Row className="g-4 mt-2">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Senha *</Form.Label>
                      <div className="position-relative">
                        <Form.Control
                          type={showPassword ? 'text' : 'password'}
                          value={formData.password}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            password: e.target.value
                          }))}
                          placeholder="Mínimo 8 caracteres"
                        />
                        <Button
                          variant="link"
                          className="position-absolute top-50 end-0 translate-middle-y pe-3 border-0"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </Button>
                      </div>
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Confirmar Senha *</Form.Label>
                      <div className="position-relative">
                        <Form.Control
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={formData.confirmPassword}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            confirmPassword: e.target.value
                          }))}
                          placeholder="Repita a senha"
                        />
                        <Button
                          variant="link"
                          className="position-absolute top-50 end-0 translate-middle-y pe-3 border-0"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                        </Button>
                      </div>
                      {formData.password !== formData.confirmPassword && formData.confirmPassword && (
                        <Form.Text className="text-danger">
                          As senhas não coincidem
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              )}

              {modalType === 'create' && formData.password && (
                <Row className="mt-3">
                  <Col>
                    <Card className="border-0 bg-light">
                      <Card.Body className="p-3">
                        <h6 className="mb-2">Requisitos de Senha:</h6>
                        <div className="row">
                          {passwordValidation.errors.length === 0 ? (
                            <div className="col-12">
                              <span className="text-success">
                                <FaCheck className="me-1" />
                                Todos os requisitos atendidos
                              </span>
                            </div>
                          ) : (
                            passwordValidation.errors.map((error, index) => (
                              <div key={index} className="col-md-6">
                                <small className="text-danger">
                                  • {error}
                                </small>
                              </div>
                            ))
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}

              <Row className="mt-4">
                <Col>
                  <Form.Check
                    type="switch"
                    label={formData.active ? "Usuário Ativo" : "Usuário Inativo"}
                    checked={formData.active}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      active: e.target.checked
                    }))}
                  />
                </Col>
              </Row>
            </div>
          </Tab>

          <Tab eventKey="sistemas" title="🏗️ Acessos aos Sistemas">
            <div className="p-4">
              <Alert variant="info">
                Selecione os sistemas que o usuário poderá acessar e defina o papel em cada um.
              </Alert>

              <Row className="g-3">
                {systems.map(system => {
                  const systemRolesForSystem = systemRoles.filter(r => r.system_id === system.id);
                  const currentAccess = formData.systemAccesses[system.id];
                  
                  return (
                    <Col md={6} key={system.id}>
                      <Card className={`h-100 ${currentAccess ? 'border-primary' : ''}`}>
                        <Card.Body>
                          <div className="d-flex align-items-center mb-3">
                            <span className="me-2" style={{ fontSize: '1.5em' }}>
                              {system.icon}
                            </span>
                            <div className="flex-grow-1">
                              <h6 className="mb-0">{system.name}</h6>
                              <small className="text-muted">{system.description}</small>
                            </div>
                            {currentAccess && (
                              <Badge bg="primary">Ativo</Badge>
                            )}
                          </div>
                          
                          <Form.Group>
                            <Form.Label>Papel no Sistema:</Form.Label>
                            <Form.Select
                              value={currentAccess || ''}
                              onChange={(e) => handleSystemAccessChange(system.id, e.target.value)}
                            >
                              <option value="">Sem acesso</option>
                              {systemRolesForSystem.map(role => (
                                <option key={role.id} value={role.id}>
                                  {role.name}
                                  {role.description && ` - ${role.description}`}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>

              {/* Seção para Vendedor - Apenas se tem role de vendedor selecionado */}
              {hasVendedorRole() && (
                <Row className="mt-4">
                  <Col>
                    <div className="border border-primary rounded-3 p-4 bg-primary-subtle">
                      <Form.Group className="mb-0">
                        <Form.Label className="fw-semibold fs-6 d-flex align-items-center text-primary">
                          <FaUserTie className="me-2" />
                          Vendedor Vinculado (Role Vendedor)
                          <span className="text-danger ms-1">*</span>
                        </Form.Label>
                        <p className="text-muted small mb-3">
                          Como este usuário tem o role de "Vendedor", é obrigatório vincular a um vendedor específico.
                        </p>
                        
                        <div className="position-relative">
                          <Form.Select
                            value={formData.vendedor_id || ''}
                            onChange={(e) => {
                              const newVendedorId = e.target.value ? parseInt(e.target.value) : null;
                              setFormData(prev => ({
                                ...prev,
                                vendedor_id: newVendedorId
                              }));
                              setVendedorValidation({ isValid: true, message: '', isChecking: false });
                            }}
                            className={`form-control-lg mt-2 ${
                              !formData.vendedor_id 
                                ? 'border-danger' 
                                : vendedorValidation.isValid 
                                  ? vendedorValidation.message === 'Vendedor disponível' ? 'border-success' : ''
                                  : 'border-danger'
                            }`}
                            required
                          >
                            <option value="">Selecione o vendedor...</option>
                            {vendedores.map(vendedor => (
                              <option key={vendedor.id} value={vendedor.id}>
                                {vendedor.descricao}
                              </option>
                            ))}
                          </Form.Select>
                          {formData.vendedor_id && vendedorValidation.isChecking && (
                            <div className="position-absolute top-50 end-0 translate-middle-y pe-3">
                              <Spinner animation="border" size="sm" />
                            </div>
                          )}
                        </div>
                        
                        {!formData.vendedor_id && (
                          <div className="invalid-feedback d-block mt-2">
                            <FaExclamationTriangle className="me-1" />
                            É obrigatório selecionar um vendedor para usuários com role "Vendedor".
                          </div>
                        )}
                        
                        {formData.vendedor_id && vendedorValidation.message && (
                          <div className={`mt-2 small ${vendedorValidation.isValid ? 'text-success' : 'text-danger'}`}>
                            {vendedorValidation.isValid ? '✅' : '❌'} {vendedorValidation.message}
                          </div>
                        )}
                        
                        <Form.Text className="text-muted mt-2 d-block">
                          <FaInfoCircle className="me-1" />
                          Este campo é obrigatório para usuários com role de vendedor. Cada vendedor só pode ser vinculado a um usuário.
                        </Form.Text>
                      </Form.Group>
                    </div>
                  </Col>
                </Row>
              )}
            </div>
          </Tab>
        </Tabs>
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancelar
        </Button>
        <Button 
          variant="primary" 
          onClick={modalType === 'create' ? handleCreateUser : handleUpdateUser}
          disabled={loading || !isFormValid()}
        >
          {loading && <Spinner animation="border" size="sm" className="me-2" />}
          {modalType === 'create' ? 'Criar Usuário' : 'Atualizar Usuário'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UserModal;